import React, { useEffect, useState } from "react";
import { Minus, Plus, Save, Loader2, Check, Coffee, Sun, Sunset, Moon, WifiOff, CloudUpload, Soup, ShoppingBasket, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError, trWeekday, trDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import QuickPurchase from "@/components/QuickPurchase";
import { cacheRows, cachedRows, enqueueSave, flushQueue, queueSize, useOnline, isNetworkError } from "@/lib/offline";
import SearchBox, { matches } from "@/components/SearchBox";

const MEALS = [
  { id: "Kahvaltı", short: "Kahvaltı", icon: Coffee },
  { id: "Öğle Yemeği", short: "Öğle", icon: Sun },
  { id: "Akşam Yemeği", short: "Akşam", icon: Sunset },
  { id: "Gece Vardiyası", short: "Gece", icon: Moon },
];
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function QuickEntry() {
  const [date, setDate] = useState(todayISO());
  const [mode, setMode] = useState("meals");
  const [meal, setMeal] = useState(MEALS[0].id);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const online = useOnline();
  const [pending, setPending] = useState(queueSize());
  const [prefilledFrom, setPrefilledFrom] = useState("");
  const [search, setSearch] = useState("");
  const shownRows = rows.filter((r) => matches(search, r.name));

  const load = () => {
    setLoading(true);
    api.get("/daily-entries", { params: { date, meal_type: meal } })
      .then((r) => { setRows(r.data); setDirty(false); cacheRows(date, meal, r.data); setPrefilledFrom(r.headers["x-prefilled-from"] || ""); })
      .catch((e) => {
        const c = cachedRows(date, meal);
        if (c) { setRows(c); setDirty(false); toast.info("Çevrimdışı — son kaydedilen liste gösteriliyor"); }
        else if (isNetworkError(e)) toast.error("Çevrimdışı ve bu gün/öğün için önbellek yok");
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, meal]);

  useEffect(() => {
    if (!online || queueSize() === 0) return;
    flushQueue().then((r) => { setPending(r.left); if (r.sent) { toast.success(`${r.sent} çevrimdışı kayıt senkronlandı`); load(); } });
    // eslint-disable-next-line
  }, [online]);

  const setHc = (id, v) => {
    const n = Math.max(0, parseInt(v, 10) || 0);
    setRows((rs) => rs.map((r) => (r.customer_id === id ? { ...r, headcount: n, total: n * r.unit_price * (1 + (r.vat_rate || 0) / 100) } : r)));
    setDirty(true);
  };
  const total = rows.reduce((a, r) => a + r.total, 0);
  const people = rows.reduce((a, r) => a + r.headcount, 0);

  const copyPrevious = async () => {
    try {
      const { data } = await api.get("/daily-entries/previous", { params: { date, meal_type: meal } });
      setRows((rs) => rs.map((r) => { const n = data.counts[r.customer_id] ?? 0; return { ...r, headcount: n, total: n * r.unit_price * (1 + (r.vat_rate || 0) / 100) }; }));
      setDirty(true); setPrefilledFrom(data.from_date);
      toast.success(`${trDate(data.from_date)} sayıları getirildi`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    const payload = { date, meal_type: meal, entries: rows.map((r) => ({ customer_id: r.customer_id, headcount: r.headcount })) };
    try {
      const { data } = await api.post("/daily-entries/save", payload);
      toast.success(data.message); load();
    } catch (e) {
      if (isNetworkError(e)) {
        const n = enqueueSave(payload);
        cacheRows(date, meal, rows.map((r) => ({ ...r, saved: r.headcount > 0 })));
        setPending(n); setDirty(false);
        toast.warning("Çevrimdışı — kayıt kuyruğa alındı, bağlantı gelince otomatik gönderilecek");
      } else toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-md mx-auto space-y-4 pb-28" data-testid="quick-entry">
      {(!online || pending > 0) && (
        <div className={`rounded-xl border px-3 py-2 text-xs flex items-center gap-2 ${online ? "border-sky-400/40 bg-sky-400/10 text-sky-400" : "border-amber-400/40 bg-amber-400/10 text-amber-400"}`} data-testid="offline-banner">
          {online ? <CloudUpload className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
          {online ? `${pending} kayıt senkronlanıyor…` : `Çevrimdışı mod${pending ? ` · ${pending} kayıt bekliyor` : ""}`}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Hızlı Giriş</h1>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 w-[150px] font-mono" data-testid="quick-date" />
        <button type="button" onClick={copyPrevious} title="Dünü Kopyala" data-testid="quick-copy-prev"
          className="h-11 w-11 shrink-0 rounded-md border border-border bg-card inline-flex items-center justify-center hover:bg-accent transition-colors"><Copy className="h-4 w-4" /></button>
        <span className="text-xs text-muted-foreground font-medium" data-testid="quick-weekday">{trWeekday(date)}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-muted/50" data-testid="quick-mode-tabs">
        {[["meals", "Porsiyon", Soup], ["purchase", "Alım (Ekmek vb.)", ShoppingBasket]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setMode(id)} data-testid={`quick-mode-${id}`}
            className={`h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${mode === id ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {mode === "purchase" ? <QuickPurchase date={date} /> : (<>
      <div className="grid grid-cols-3 gap-2" data-testid="quick-meal-tabs">
        {MEALS.map((m) => {
          const Icon = m.icon; const on = meal === m.id;
          return (
            <button key={m.id} onClick={() => setMeal(m.id)} data-testid={`quick-meal-${m.short}`}
              className={`h-12 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-accent"}`}>
              <Icon className="h-4 w-4" /> {m.short}
            </button>
          );
        })}
      </div>

      <SearchBox value={search} onChange={setSearch} placeholder="Müşteri ara…" testId="quick-search-input" />

      <div className="space-y-2">
        {loading && <div className="text-center text-sm text-muted-foreground py-8">Yükleniyor…</div>}
        {!loading && rows.length > 0 && shownRows.length === 0 && <div className="text-center text-sm text-muted-foreground py-6" data-testid="quick-search-empty">Aramayla eşleşen müşteri yok</div>}
        {!loading && rows.length === 0 && <div className="text-center text-sm text-muted-foreground py-8" data-testid="quick-empty">Bu öğün için fiyatı tanımlı müşteri yok.</div>}
        {!loading && prefilledFrom && <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="quick-prefilled-banner">{trDate(prefilledFrom)} sayıları otomatik getirildi — kontrol edip kaydedin.</div>}
        {!loading && shownRows.map((r) => (
          <div key={r.customer_id} data-testid={`quick-row-${r.customer_id}`} className={`rounded-xl border p-3 flex items-center gap-3 ${r.saved && !dirty ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-card"}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate text-sm flex items-center gap-1.5">{r.name}{r.saved && <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />}</div>
              <div className="text-[11px] text-muted-foreground font-mono">{TRY(r.unit_price * (1 + (r.vat_rate || 0) / 100))}{r.vat_rate > 0 && <span className="text-primary"> KDV dahil</span>} × {r.headcount} = <span className="text-foreground font-semibold">{TRY(r.total)}</span></div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setHc(r.customer_id, r.headcount - 10)} data-testid={`quick-minus-${r.customer_id}`} className="h-11 w-11 rounded-lg border border-border flex items-center justify-center active:bg-accent text-lg font-bold"><Minus className="h-4 w-4" /></button>
              <input type="number" inputMode="numeric" value={r.headcount} onChange={(e) => setHc(r.customer_id, e.target.value)} onFocus={(e) => e.target.select()}
                data-testid={`quick-input-${r.customer_id}`} className="h-11 w-16 rounded-lg border border-border bg-background text-center font-mono text-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary" />
              <button onClick={() => setHc(r.customer_id, r.headcount + 10)} data-testid={`quick-plus-${r.customer_id}`} className="h-11 w-11 rounded-lg border border-border flex items-center justify-center active:bg-accent"><Plus className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-background/95 backdrop-blur px-4 py-3">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{people} kişi · {MEALS.find((m) => m.id === meal)?.short}</div>
            <div className="font-mono text-lg font-bold text-primary truncate" data-testid="quick-total">{TRY(total)}</div>
          </div>
          <button onClick={save} disabled={saving || !dirty} data-testid="quick-save"
            className="h-12 px-6 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
          </button>
        </div>
      </div>
      </>)}
    </div>
  );
}
